import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { formatDateTime } from "./servicesUtils";

// Just the label:value grid, with no toggle - reused by InternalJobMeta
// below (self-toggling) and by DetailsNotesTabs (externally controlled).
export function InternalJobMetaContent({ job, dense = false }) {
  if (!job) return null;

  const rows = [
    ["Job ID", job.pk_id],
    ["Customer", job.customer_name || job.customer_id],
    ["Device", job.device_name || job.device_id],
    ["Manufacturer", job.device_manufacturer],
    ["Model", job.device_model],
    ["Room Name", job.room_name],
    ["Address", job.location_address],
    ["Address 2", job.location_address_2],
    ["City", job.location_city],
    ["County", job.location_state],
    ["Postcode", job.location_postcode],
    ["Service type", job.service_type_name],
    ["Route", job.route_name],
    ["Generation source", job.generation_source],
    ["Scheduled start", formatDateTime(job.scheduled_start)],
    ["Scheduled end", formatDateTime(job.scheduled_end)],
    ["Actual start", formatDateTime(job.actual_start)],
    ["Actual end", formatDateTime(job.actual_end)],
  ].filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );

  if (rows.length === 0) return null;

  return (
    <Box
      sx={{
        p: dense ? 0.75 : 1,
        borderRadius: 1.5,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "action.hover",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 0.4,
      }}
    >
      {rows.map(([label, value]) => (
        <Typography
          key={label}
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: dense ? "0.68rem" : "0.75rem" }}
        >
          <strong>{label}:</strong> {String(value)}
        </Typography>
      ))}
    </Box>
  );
}

// Compact label:value grid of additional job fields, shown only in the
// "internal" (Admin/User) view - technicians never see this block.
export default function InternalJobMeta({ job, dense = false }) {
  const [open, setOpen] = useState(false);

  if (!job) return null;

  return (
    <Box sx={{ mt: dense ? 0.75 : 1 }}>
      <Box
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.4,
          cursor: "pointer",
          userSelect: "none",
          width: "fit-content",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: dense ? "1rem" : "1rem", fontWeight: 600 }}
        >
          Details
        </Typography>
        <ExpandMoreOutlinedIcon
          sx={{
            fontSize: dense ? 14 : 16,
            color: "text.secondary",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
        />
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: dense ? 0.5 : 0.75 }}>
          <InternalJobMetaContent job={job} dense={dense} />
        </Box>
      </Collapse>
    </Box>
  );
}
